import {
    useMutation,
    UseMutationOptions,
    useQueryClient,
} from '@tanstack/react-query'
import {
    CreateInviteLinksGroupDtoForSites,
    UpdateInviteLinksGroupDto,
} from '@synapex/shared'
import { inviteLinksGroupService } from './invite-links-group.service'
import { InviteLinksGroupSitesType } from './invite-links-group.types'
import { LinksQueryKeyConstants } from './invite-links-group.query.constants'

export function useCreateInviteLinksGroupSitesMutation(
    options?: UseMutationOptions<
        InviteLinksGroupSitesType,
        Error,
        CreateInviteLinksGroupDtoForSites
    >
) {
    const queryClient = useQueryClient()

    return useMutation<
        InviteLinksGroupSitesType,
        Error,
        CreateInviteLinksGroupDtoForSites
    >({
        mutationFn: (data: CreateInviteLinksGroupDtoForSites) =>
            inviteLinksGroupService.createGroupForSites(data),
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: [LinksQueryKeyConstants.GroupsSites, data.site_id],
            })
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

interface UpdateInviteLinksGroupVariables {
    id: number
    data: UpdateInviteLinksGroupDto
}

export function useUpdateInviteLinksGroupSitesMutation(
    options?: UseMutationOptions<
        InviteLinksGroupSitesType,
        Error,
        UpdateInviteLinksGroupVariables
    >
) {
    const queryClient = useQueryClient()

    return useMutation<
        InviteLinksGroupSitesType,
        Error,
        UpdateInviteLinksGroupVariables
    >({
        mutationFn: ({ id, data }: UpdateInviteLinksGroupVariables) =>
            inviteLinksGroupService.updateGroupForSites(id, data),
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: [LinksQueryKeyConstants.GroupsSites, data.site_id],
            })
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useDeleteInviteLinksGroupSitesMutation(
    options?: UseMutationOptions<number, Error, number>
) {
    const queryClient = useQueryClient()

    return useMutation<number, Error, number>({
        mutationFn: (id: number) =>
            inviteLinksGroupService.deleteGroupForSites(id),
        ...options,
        onSuccess: (siteId, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: [LinksQueryKeyConstants.GroupsSites, siteId],
            })
            if (options?.onSuccess) {
                options.onSuccess(siteId, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}
